import React, { useRef, useState } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// ⓘ button revealing a wide bubble on hover (desktop) or tap (mobile, stays open until a tap elsewhere).
export function InfoTip({ content, side = "top", testid, light = false }) {
  const [open, setOpen] = useState(false);
  const openedAt = useRef(0);
  const change = (v) => {
    if (v) openedAt.current = Date.now();
    setOpen(v);
  };
  // Touch screens: close the bubble when the page scrolls, so it never stays stuck on screen.
  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, { capture: true });
  }, [open]);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={change}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Information"
            data-testid={testid}
            className={`inline-flex items-center justify-center align-middle p-1 focus:outline-none transition-colors ${
              light ? "text-white/85 hover:text-white" : "text-muted-foreground hover:text-foreground focus:text-foreground"
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Touch: Radix opens on focus just before click — don't toggle it shut again.
              if (Date.now() - openedAt.current < 400) return setOpen(true);
              change(!open);
            }}
          >
            <Info className={light ? "w-4 h-4" : "w-3.5 h-3.5"} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          collisionPadding={12}
          onPointerDownOutside={() => setOpen(false)}
          data-testid={testid ? `${testid}-bubble` : undefined}
          className="max-w-[min(560px,calc(100vw-24px))] whitespace-normal leading-relaxed bg-[#002FA7] text-white font-sans text-[13.5px] px-4 py-3 shadow-xl"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
